import {
  BuyerQuote,
  BuyerQuoteSkeleton,
  Endorsements,
  FloatingCTAButton,
  MilesProgramCard,
  NoOfferAlert,
  OfferReady,
  OfferReadySkeleton,
  RequestAlert,
  SecureQuoteErrorAlert,
  TotalPayout,
  TrustIndicators,
} from "@/components/dashboard";
import {
  MilesProgramCardSkeleton,
  TotalPayoutSkeleton,
} from "@/components/dashboard/skeletons";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useGetCustomerById,
  useGetDealByTemporaryId,
  useGetProductById,
} from "@/hooks";
import {
  calculateDynamicRate,
  calculateTotalPayout,
  type MilesProgram,
  updateProgramStatus,
} from "@/lib/miles-calculations";
import {
  ErrorTypes,
  TransactionTypes,
  useDashboardStore,
  useSecureQuoteStore,
  WarnTypes,
} from "@/stores";
import { useUserCredentialsStore } from "@/stores/user-credentials";
import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { InactivityDialog } from "@/components/dashboard/InactivityDialog";
import { SecureLoader } from "@/components/dashboard/SecureLoader";
import { DEAL_STAGES } from "@/core";
import { StatusTier } from "@/core/dtos/status-tier";
import { serviceHost } from "@/services";
import {
  buildCustomerPortalAnalyticsPayload,
  type CustomerPortalAnalyticsDealCondition,
  pushCustomerPortalAnalyticsEvent,
  savePendingDealSubmittedPayload,
  WEBFLOW_BASE_URL,
} from "@/utils";
import { useNavigate, useSearchParams } from "react-router-dom";

const INACTIVITY_TIME = 1 * 60 * 1000; // 1 minute

export function ViewOfferPage() {
  const [emailError, setEmailError] = useState(false);
  const [inactivityDialogOpen, setInactivityDialogOpen] = useState(false);
  const [hasShownInactivityDialog, setHasShownInactivityDialog] =
    useState(false);
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasTrackedPageOpenRef = useRef(false);
  const [searchParams] = useSearchParams();
  const temporaryDealId = searchParams.get("temporaryDealId");
  const navigate = useNavigate();

  const {
    data: temporaryDeal,
    isLoading: isTemporaryDealLoading,
    isFetching: isTemporaryDealFetching,
  } = useGetDealByTemporaryId(temporaryDealId ?? "");

  const {
    data: product,
    isLoading: isProductLoading,
    isError: isProductError,
    isFetching: isProductFetching,
  } = useGetProductById(temporaryDeal?.productId || "");

  const {
    isCreatingNewQuote,
    warnState,
    isSecuringQuote,
    errorDetails,
    errorState,
    setErrorState,
    clearErrorState,
    setWarnState,
    setSanitizedMiles,
    program,
    setProgram,
    setIsSecuringQuote,
  } = useDashboardStore();

  const setTransactionType = useUserCredentialsStore(
    (state) => state.setTransactionType,
  );
  const setCustomerId = useUserCredentialsStore((state) => state.setCustomerId);
  const storedCustomerId = useUserCredentialsStore((state) => state.customerId);
  const fallbackEmail = useUserCredentialsStore((state) => state.email);
  const fallbackFirstName = useUserCredentialsStore((state) => state.firstName);
  const fallbackLastName = useUserCredentialsStore((state) => state.lastName);
  const fallbackPhoneNumber = useUserCredentialsStore(
    (state) => state.phoneNumber,
  );
  const addQuoteId = useSecureQuoteStore((s) => s.addQuoteId);
  const functionsService = serviceHost.getFunctionsService();
  const resolvedCustomerId = temporaryDeal?.customerId || storedCustomerId;
  const customerQuery = useGetCustomerById(resolvedCustomerId);

  const transactionType =
    temporaryDeal?.buyOrSell === "buy"
      ? TransactionTypes.BUY
      : TransactionTypes.SELL;
  // Before the quote loads, the form the customer just sent says which it is.
  const storedTransactionType = useUserCredentialsStore(
    (state) => state.transactionType,
  );
  const isBuyQuote = temporaryDeal
    ? transactionType === TransactionTypes.BUY
    : storedTransactionType === TransactionTypes.BUY;

  useEffect(() => {
    setTransactionType(transactionType);
    setCustomerId(temporaryDeal?.customerId || "");
  }, [transactionType, setTransactionType, setCustomerId, temporaryDeal]);

  // Clear error state when dealId changes (new deal opened)
  useEffect(() => {
    clearErrorState();
  }, [temporaryDealId, clearErrorState]);

  // Check if deal has expired: at its own expiry date for quotes created in
  // the admin portal, otherwise 10 minutes from createdAt or updatedAt
  const isDealExpired = useMemo(() => {
    if (!temporaryDeal || temporaryDeal.archived) return true;

    if (temporaryDeal.expiresAt) {
      return temporaryDeal.expiresAt.getTime() <= Date.now();
    }

    const expirationTime = 10 * 60 * 1000; // 10 minutes in milliseconds
    const lastUpdated = temporaryDeal.updatedAt || temporaryDeal.createdAt;

    if (!lastUpdated) return false;

    const now = new Date();
    const dealAge = now.getTime() - lastUpdated.getTime();

    return dealAge > expirationTime;
  }, [temporaryDeal]);

  useEffect(() => {
    if (!temporaryDeal && !isTemporaryDealLoading && !isTemporaryDealFetching) {
      setErrorState(ErrorTypes.ERROR);
    } else if (temporaryDeal && isDealExpired) {
      setErrorState(ErrorTypes.ERROR);
    } else if (temporaryDeal && !isDealExpired) {
      // Clear error state when a valid (non-expired) deal is loaded
      clearErrorState();
    }
  }, [
    temporaryDeal,
    isTemporaryDealLoading,
    isTemporaryDealFetching,
    isDealExpired,
    setErrorState,
    clearErrorState,
  ]);

  // Every quote comes through this page. Null means it was priced as
  // submitted; anything else means we are showing an adjusted offer.
  const offerCondition: CustomerPortalAnalyticsDealCondition | null =
    useMemo(() => {
      if (!temporaryDeal || !product) return null;

      const isSell = transactionType === TransactionTypes.SELL;
      const minValue = isSell
        ? product.minimumValueForSell
        : product.minimumValueForBuy;
      const maxValue = isSell
        ? product.maximumValueForSell
        : product.maximumValueForBuy;

      if (minValue && temporaryDeal.quantity < minValue) return "under_minimum";
      if (maxValue && temporaryDeal.quantity > maxValue) return "over_maximum";
      if (
        temporaryDeal.stage === DEAL_STAGES.SELL_BUSINESS_LOST ||
        temporaryDeal.stage === DEAL_STAGES.BUY_BUSINESS_LOST
      ) {
        return "rejected";
      }

      return null;
    }, [temporaryDeal, product, transactionType]);

  const temporaryProgram: MilesProgram | null = useMemo(() => {
    if (!temporaryDeal || !product) return null;

    const minValue =
      transactionType === TransactionTypes.SELL
        ? product.minimumValueForSell
        : product.minimumValueForBuy;
    const maxValue =
      transactionType === TransactionTypes.SELL
        ? product.maximumValueForSell
        : product.maximumValueForBuy;

    let clampedQuantity = temporaryDeal.quantity;
    if (minValue && clampedQuantity < minValue) {
      clampedQuantity = minValue;
    } else if (maxValue && clampedQuantity > maxValue) {
      clampedQuantity = maxValue;
    }

    // A rate staff set on an admin quote holds at any quantity, so it stands
    // in for the product's tiers everywhere the program is re-priced.
    const overrideTiers = temporaryDeal.unitPriceOverride
      ? [
          {
            minRange: 0,
            maxRange: Number.MAX_SAFE_INTEGER,
            price: temporaryDeal.unitPriceOverride,
          },
        ]
      : null;
    const sellPrices = overrideTiers ?? product.sellPrices ?? [];
    const buyPrices = overrideTiers ?? product.buyPrices ?? [];

    const ratePerMile =
      transactionType === TransactionTypes.SELL
        ? sellPrices[0]?.price || 0
        : buyPrices[0]?.price || 0;

    // Calculate dynamic rate based on miles
    const dynamicRate = calculateDynamicRate({
      miles: clampedQuantity,
      transactionType,
      sellPrices,
      buyPrices,
      fallbackRate: ratePerMile,
    });

    // Get initial loyalty status from temporaryDeal
    const initialLoyaltyStatus = temporaryDeal.statusTier ?? null;

    // Calculate total payout including status bonus and promo code discount
    const { statusBonusAmount, totalPayout } = calculateTotalPayout({
      totalMiles: clampedQuantity,
      dynamicRate,
      loyaltyStatus: initialLoyaltyStatus,
      transactionType,
      promoCode: temporaryDeal.promoCode,
    });

    return {
      id: temporaryDeal.id,
      programId: product.id,
      airline: product.name,
      logo: product.logo || "",
      loyaltyStatus: initialLoyaltyStatus,
      ratePerMile: dynamicRate,
      statusBonus: statusBonusAmount,
      fingerprint: temporaryDeal.fingerprints[0] || null,
      totalPayout,
      sanitizedMiles: clampedQuantity,
      name: product.name,
      buyOrSell: product.buyOrSell,
      category: product.category,
      inactive: product.inactive || false,
      accountDetails: {
        firstName: "",
        lastName: "",
        frequentFlyerNumber: "",
        accountEmail: "",
        receivedMilesBefore: "",
        notes: "",
      },
      statusTiers: product.statusTiers || [],
      sellPrices,
      buyPrices,
      minimumValueForSell: product.minimumValueForSell || 0,
      maximumValueForSell: product.maximumValueForSell || 0,
      minimumValueForBuy: product.minimumValueForBuy || 0,
      maximumValueForBuy: product.maximumValueForBuy || 0,
      usernameLabelOverride: product.usernameLabelOverride,
      emailLabelOverride: product.emailLabelOverride,
      passwordLabelOverride: product.passwordLabelOverride,
      promoCode: temporaryDeal.promoCode,
    };
  }, [temporaryDeal, product, transactionType]);

  // Set the temporaryProgram in the store
  useEffect(() => {
    if (temporaryProgram) {
      setProgram(temporaryProgram);
    }
  }, [temporaryProgram, setProgram]);

  // Staff fixed the miles and price on an admin quote and its deal is already
  // in HubSpot, so a seller goes straight to their account and payout details.
  const isAdminQuote = temporaryDeal?.source === "admin";
  useEffect(() => {
    if (
      isAdminQuote &&
      transactionType === TransactionTypes.SELL &&
      temporaryProgram &&
      !isDealExpired
    ) {
      navigate("/order-summary", { replace: true });
    }
  }, [
    isAdminQuote,
    isDealExpired,
    navigate,
    temporaryProgram,
    transactionType,
  ]);

  useEffect(() => {
    if (hasTrackedPageOpenRef.current || !temporaryProgram || isDealExpired) {
      return;
    }

    if (
      isProductLoading ||
      isProductFetching ||
      isTemporaryDealLoading ||
      isTemporaryDealFetching
    ) {
      return;
    }

    if (resolvedCustomerId && !customerQuery.isFetched) {
      return;
    }

    // offer_view_alternative is a Google Ads conversion for adjusted offers;
    // a quote priced as submitted records offer_view on the order summary.
    if (!offerCondition) {
      hasTrackedPageOpenRef.current = true;
      return;
    }

    const payload = buildCustomerPortalAnalyticsPayload({
      event: "offer_view_alternative",
      customer: customerQuery.data,
      fallbackProfile: {
        email: fallbackEmail,
        firstName: fallbackFirstName,
        lastName: fallbackLastName,
        phoneNumber: fallbackPhoneNumber,
      },
      offerType: transactionType,
      milesPointsType: temporaryProgram.name || product?.name,
      amount_of_miles:
        temporaryDeal?.quantity || temporaryProgram.sanitizedMiles,
      amount: temporaryProgram.totalPayout,
      bonusTier: temporaryProgram.loyaltyStatus,
      dealCondition: offerCondition,
    });

    pushCustomerPortalAnalyticsEvent({ payload });
    hasTrackedPageOpenRef.current = true;
  }, [
    customerQuery.data,
    customerQuery.isFetched,
    fallbackEmail,
    fallbackFirstName,
    fallbackLastName,
    fallbackPhoneNumber,
    isDealExpired,
    isProductFetching,
    isProductLoading,
    isTemporaryDealFetching,
    isTemporaryDealLoading,
    offerCondition,
    product?.name,
    resolvedCustomerId,
    temporaryDeal?.quantity,
    temporaryProgram,
    transactionType,
  ]);

  useEffect(() => {
    if (!temporaryDeal || !product) {
      setWarnState(WarnTypes.NONE);
      return;
    }

    const quantity = temporaryDeal.quantity;
    const minValue =
      transactionType === TransactionTypes.SELL
        ? product.minimumValueForSell
        : product.minimumValueForBuy;
    const maxValue =
      transactionType === TransactionTypes.SELL
        ? product.maximumValueForSell
        : product.maximumValueForBuy;

    if (minValue && quantity < minValue) {
      setWarnState(WarnTypes.UNDER);
    } else if (maxValue && quantity > maxValue) {
      setWarnState(WarnTypes.OVER);
    } else {
      setWarnState(WarnTypes.NONE);
    }
  }, [temporaryDeal, product, transactionType, setWarnState]);

  const handleStatusChange = (
    programId: string,
    newStatus: StatusTier | null,
  ) => {
    // Re-price what the customer has now, not the quote as first loaded, so
    // an adjusted quantity survives a status change.
    const currentProgram =
      program?.programId === programId ? program : temporaryProgram;
    if (currentProgram && currentProgram.programId === programId) {
      const updatedProgram = updateProgramStatus(currentProgram, newStatus);

      const { statusBonusAmount, totalPayout } = calculateTotalPayout({
        totalMiles: updatedProgram.sanitizedMiles,
        dynamicRate: updatedProgram.ratePerMile,
        loyaltyStatus: newStatus,
        transactionType,
        promoCode: updatedProgram.promoCode,
      });

      const finalUpdatedProgram = {
        ...updatedProgram,
        statusBonus: statusBonusAmount,
        totalPayout,
      };

      setProgram(finalUpdatedProgram);
    }
  };

  // Check if any dialog is currently open
  const isAnyDialogOpen = useCallback(() => {
    // Check for Radix UI Dialog elements (they have data-state="open" when open)
    const openDialogs = document.querySelectorAll(
      '[role="dialog"][data-state="open"]',
    );
    // Also check for emailError dialog state
    return openDialogs.length > 0 || emailError;
  }, [emailError]);

  // Reset inactivity timer
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    // Set timer if program is available
    if (program) {
      inactivityTimerRef.current = setTimeout(() => {
        // Only show inactivity dialog if no other dialog is open
        if (!isAnyDialogOpen()) {
          setInactivityDialogOpen(true);
          setHasShownInactivityDialog(true);
        }
        // If another dialog is open, the timer will naturally expire
        // and won't show the inactivity dialog until the other dialog closes
      }, INACTIVITY_TIME);
    }
  }, [program, isAnyDialogOpen]);

  // Handle form interaction events (only form interactions reset the timer)
  useEffect(() => {
    if (!program) {
      return;
    }

    // Form-related events that should reset the inactivity timer
    const formActivityEvents = [
      "input", // Input field changes
      "change", // Select, checkbox, radio changes
      "focus", // Form field focus
      "blur", // Form field blur (user interacted with field)
    ];

    // Mouse/touch events for slider interactions
    const sliderInteractionEvents = ["mousedown", "touchstart"];

    const handleFormActivity = (event: Event) => {
      const target = event.target as HTMLElement;

      // Only reset timer for actual form elements
      const isFormElement =
        target.tagName === "INPUT" ||
        target.tagName === "SELECT" ||
        target.tagName === "TEXTAREA" ||
        target.closest('[role="combobox"]') !== null || // Select component
        target.closest('[role="slider"]') !== null || // Slider component
        target.closest('[data-slot="slider"]') !== null || // Slider component (Radix UI)
        target.closest('[data-slot="slider-thumb"]') !== null || // Slider thumb
        target.closest('[data-slot="slider-track"]') !== null || // Slider track
        target.closest('input[type="range"]') !== null; // Range input

      if (isFormElement) {
        // Reset the flag when user becomes active again
        if (hasShownInactivityDialog) {
          setHasShownInactivityDialog(false);
        }
        resetInactivityTimer();
      }
    };

    const handleSliderInteraction = (event: Event) => {
      const target = event.target as HTMLElement;

      // Check if interaction is with slider elements
      const isSliderElement =
        target.closest('[role="slider"]') !== null ||
        target.closest('[data-slot="slider"]') !== null ||
        target.closest('[data-slot="slider-thumb"]') !== null ||
        target.closest('[data-slot="slider-track"]') !== null;

      if (isSliderElement) {
        // Reset the flag when user becomes active again
        if (hasShownInactivityDialog) {
          setHasShownInactivityDialog(false);
        }
        resetInactivityTimer();
      }
    };

    // Use event delegation on document to catch all form interactions
    formActivityEvents.forEach((event) => {
      document.addEventListener(event, handleFormActivity, {
        passive: true,
        capture: true,
      });
    });

    // Listen for slider-specific interactions
    sliderInteractionEvents.forEach((event) => {
      document.addEventListener(event, handleSliderInteraction, {
        passive: true,
        capture: true,
      });
    });

    // Start the timer when component mounts
    resetInactivityTimer();

    return () => {
      formActivityEvents.forEach((event) => {
        document.removeEventListener(event, handleFormActivity, {
          capture: true,
        });
      });
      sliderInteractionEvents.forEach((event) => {
        document.removeEventListener(event, handleSliderInteraction, {
          capture: true,
        });
      });
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [program, hasShownInactivityDialog, resetInactivityTimer]);

  // Reset timer when emailError dialog closes (user interacted by closing it)
  useEffect(() => {
    if (!emailError && program) {
      // Dialog was closed, reset the inactivity timer
      resetInactivityTimer();
    }
  }, [emailError, program, resetInactivityTimer]);

  const trackApproveOfferSuccess = useCallback(() => {
    if (!program) {
      return;
    }

    const sharedPayloadArgs = {
      customer: customerQuery.data,
      fallbackProfile: {
        email: fallbackEmail,
        firstName: fallbackFirstName,
        lastName: fallbackLastName,
        phoneNumber: fallbackPhoneNumber,
      },
      offerType: transactionType,
      milesPointsType: program.name || product?.name,
      amount_of_miles: program.sanitizedMiles,
      amount: program.totalPayout,
      payoutMethod: temporaryDeal?.paymentMethod,
      bonusTier: program.loyaltyStatus,
    };

    pushCustomerPortalAnalyticsEvent({
      payload: buildCustomerPortalAnalyticsPayload({
        event: "deal_approve_offer",
        ...sharedPayloadArgs,
      }),
    });

    savePendingDealSubmittedPayload({
      payload: buildCustomerPortalAnalyticsPayload({
        event: "deal_submitted",
        ...sharedPayloadArgs,
      }),
    });
  }, [
    customerQuery.data,
    fallbackEmail,
    fallbackFirstName,
    fallbackLastName,
    fallbackPhoneNumber,
    product?.name,
    program,
    temporaryDeal?.paymentMethod,
    transactionType,
  ]);

  const createSecureQuoteAndNavigate = useCallback(
    async ({
      redirectTo,
    }: {
      redirectTo: "order-summary" | "buy-thank-you";
    }) => {
      if (!program || isSecuringQuote) {
        return;
      }

      setIsSecuringQuote(true);

      try {
        const quotePromise = functionsService.createSecureQuote({
          temporaryDealId: program.id,
          quantity: program.sanitizedMiles,
        });

        await new Promise((resolve) => setTimeout(resolve, 3300));

        const dealId = await quotePromise;
        trackApproveOfferSuccess();

        if (redirectTo === "order-summary") {
          addQuoteId(dealId);
          navigate("/order-summary");
          return;
        }

        window.location.href = `${WEBFLOW_BASE_URL}/buy-miles-thank-you`;
      } catch (error) {
        console.error("Error creating secure quote:", error);

        const errorDetails = {
          message:
            error instanceof Error
              ? error.message
              : "Failed to create secure quote",
          code: "SECURE_QUOTE_ERROR",
          timestamp: new Date(),
          status:
            (error as { status?: number; response?: { status?: number } })
              ?.status ||
            (error as { response?: { status?: number } })?.response?.status,
        };

        setErrorState(ErrorTypes.SECURE_QUOTE_ERROR, errorDetails);
        navigate(`/view-offer?temporaryDealId=${program?.id || ""}`);
      } finally {
        setIsSecuringQuote(false);
      }
    },
    [
      addQuoteId,
      functionsService,
      isSecuringQuote,
      navigate,
      program,
      setErrorState,
      setIsSecuringQuote,
      trackApproveOfferSuccess,
    ],
  );

  const handlePrimaryOfferApproval = useCallback(async () => {
    if (!program || isSecuringQuote) {
      return;
    }

    if (transactionType === TransactionTypes.SELL) {
      await createSecureQuoteAndNavigate({
        redirectTo: "order-summary",
      });
      return;
    }

    setIsSecuringQuote(true);

    try {
      await new Promise((resolve) => setTimeout(resolve, 3300));
      await functionsService.emailQuote({
        temporaryDealId: program.id,
        quantity: program.sanitizedMiles,
      });
      trackApproveOfferSuccess();
      navigate("/email-thank-you");
    } catch (error) {
      console.error("Email quote error:", error);
      setEmailError(true);
    } finally {
      setIsSecuringQuote(false);
    }
  }, [
    createSecureQuoteAndNavigate,
    functionsService,
    isSecuringQuote,
    navigate,
    program,
    setIsSecuringQuote,
    trackApproveOfferSuccess,
    transactionType,
  ]);

  // A buyer secures the quote with an agent on WhatsApp; settle it so the
  // agent finds the order and the customer gets the quote email.
  const hasSettledBuyQuoteRef = useRef(false);
  const handleBuyerSecure = useCallback(() => {
    if (!program || hasSettledBuyQuoteRef.current) {
      return;
    }
    hasSettledBuyQuoteRef.current = true;
    functionsService
      .emailQuote({
        temporaryDealId: program.id,
        quantity: program.sanitizedMiles,
      })
      .then(() => trackApproveOfferSuccess())
      .catch((error) => {
        console.error("Email quote error:", error);
        hasSettledBuyQuoteRef.current = false;
      });
  }, [functionsService, program, trackApproveOfferSuccess]);

  // Handle secure quote from inactivity dialog
  const handleInactivitySecureQuote = async () => {
    setInactivityDialogOpen(false);

    if (transactionType === TransactionTypes.SELL) {
      await createSecureQuoteAndNavigate({
        redirectTo: "order-summary",
      });
      return;
    }

    await createSecureQuoteAndNavigate({
      redirectTo: "buy-thank-you",
    });
  };

  // Handle email quote from inactivity dialog
  const handleInactivityEmailQuote = async () => {
    if (!program) {
      return;
    }
    try {
      await functionsService.emailQuote({
        temporaryDealId: program.id,
        quantity: program.sanitizedMiles,
      });
      trackApproveOfferSuccess();
      navigate("/email-thank-you");
    } catch (error) {
      console.error("Email quote error:", error);
      setEmailError(true);
    }
  };

  const offersRef = useRef<HTMLDivElement>(null);
  // Until the quote and its product load we can't tell whether it was
  // adjusted. Most aren't, so hold the new layout's shape meanwhile.
  const isOfferLoading =
    isTemporaryDealLoading ||
    (!!temporaryDeal && !temporaryDeal.archived && !product && !isProductError);
  // The store copy carries the customer's edits but lands a render after
  // the quote loads; until then show the quote itself.
  const offerProgram =
    program && program.id === temporaryProgram?.id ? program : temporaryProgram;
  // A quote priced as submitted gets the streamlined offer; adjusted offers
  // keep the editable layout below.
  const showOfferReady =
    !offerCondition &&
    !isDealExpired &&
    !isCreatingNewQuote &&
    offerProgram !== null;
  return (
    <div
      className={`min-h-screen w-full ${showOfferReady || isOfferLoading ? "bg-[#F4F6FB]" : ""}`}
    >
      {isSecuringQuote && <SecureLoader />}
      <div className=" flex flex-col">
        {isOfferLoading ? (
          isBuyQuote ? (
            <BuyerQuoteSkeleton />
          ) : (
            <OfferReadySkeleton />
          )
        ) : showOfferReady && offerProgram && isBuyQuote ? (
          <div ref={offersRef}>
            <BuyerQuote
              program={offerProgram}
              alert={
                errorState === ErrorTypes.SECURE_QUOTE_ERROR && (
                  <SecureQuoteErrorAlert
                    company={product?.name ?? ""}
                    transactionType={transactionType}
                    error={errorDetails || undefined}
                  />
                )
              }
              onSecure={handleBuyerSecure}
              onChangeMiles={
                isAdminQuote
                  ? undefined
                  : (miles) => setSanitizedMiles({ miles, transactionType })
              }
            />
          </div>
        ) : showOfferReady && offerProgram ? (
          <div ref={offersRef}>
            <OfferReady
              program={offerProgram}
              alert={
                errorState === ErrorTypes.SECURE_QUOTE_ERROR && (
                  <SecureQuoteErrorAlert
                    company={product?.name ?? ""}
                    transactionType={transactionType}
                    error={errorDetails || undefined}
                  />
                )
              }
              onAccept={handlePrimaryOfferApproval}
              onChange={({ miles, status }) => {
                // The status re-prices the current quantity, so it goes first.
                handleStatusChange(offerProgram.programId, status);
                setSanitizedMiles({ miles, transactionType });
              }}
            />
          </div>
        ) : (
          <>
            <div className="flex w-full items-center justify-between bg-[#063e9f] text-white  pb-[4%] pt-[6%] md:pb-[2%] md:pt-[4%]">
              <div className="w-full md:w-[1500px] md:mx-auto md:flex md:items-center md:justify-between px-4">
                <h2 className="text-center md:text-left w-full text-3xl xl:text-5xl font-semibold xl:w-1/3 overflow-hidden">
                  {offerCondition ? "Our Updated Offer" : "Your Offer"}
                </h2>
                <div className="hidden xl:block">
                  <TrustIndicators />
                </div>
              </div>
            </div>

            {!isCreatingNewQuote && (
              <>
                <div ref={offersRef} className={`w-full relative`}>
                  <span className="absolute min-h-1/2 max-h-1/2 h-full   bg-[#063e9f] z-10 w-full" />
                  <div className="max-w-[1500px] min-h-[500px] xl:min-h-0 flex flex-col xl:flex-row mx-auto justify-between gap-3 xl:gap-10 w-full relative z-10 px-4">
                    <div className="order-2 xl:order-1">
                      {isProductLoading || isProductFetching ? (
                        <TotalPayoutSkeleton />
                      ) : !isDealExpired ? (
                        <TotalPayout
                          onEmailError={setEmailError}
                          onSecureQuote={handlePrimaryOfferApproval}
                        />
                      ) : null}
                    </div>
                    <div className="order-1 xl:order-2 w-full flex-1 flex flex-col gap-3 xl:gap-10">
                      {errorState === ErrorTypes.ERROR && (
                        <>
                          <NoOfferAlert />
                        </>
                      )}
                      {errorState === ErrorTypes.SECURE_QUOTE_ERROR && (
                        <SecureQuoteErrorAlert
                          company={product?.name ?? ""}
                          transactionType={transactionType}
                          error={errorDetails || undefined}
                        />
                      )}
                      {warnState !== WarnTypes.NONE &&
                        !isCreatingNewQuote &&
                        temporaryProgram && (
                          <RequestAlert
                            type={warnState}
                            company={product?.name ?? ""}
                            enteredValue={temporaryDeal?.quantity ?? 0} // TO DO: CHANGE THIS
                            targetValue={
                              warnState === WarnTypes.UNDER
                                ? transactionType === TransactionTypes.SELL
                                  ? (product?.minimumValueForSell ?? 0)
                                  : (product?.minimumValueForBuy ?? 0)
                                : transactionType === TransactionTypes.SELL
                                  ? (product?.maximumValueForSell ?? 0)
                                  : (product?.maximumValueForBuy ?? 0)
                            }
                          />
                        )}

                      {isProductLoading ||
                      isProductFetching ||
                      isTemporaryDealLoading ||
                      isTemporaryDealFetching ? (
                        <MilesProgramCardSkeleton />
                      ) : program !== null && !isDealExpired ? (
                        <div>
                          <MilesProgramCard
                            key={program.programId}
                            transactionType={transactionType}
                            airline={program.airline}
                            logo={
                              program.logo && program.logo !== ""
                                ? program.logo
                                : "/tmm-dark-logo.svg"
                            }
                            loyaltyStatus={program.loyaltyStatus}
                            totalMiles={program.sanitizedMiles ?? 0}
                            rejectedMilesAmount={
                              offerCondition
                                ? temporaryDeal?.quantity
                                : undefined
                            }
                            quoteLabel={
                              offerCondition ? "Updated Quote" : "Your Quote"
                            }
                            ratePerMile={program.ratePerMile}
                            onMilesChange={(miles) =>
                              setSanitizedMiles({
                                miles,
                                transactionType,
                              })
                            }
                            onStatusChange={(status: StatusTier | null) =>
                              handleStatusChange(program.programId, status)
                            }
                            statusTiers={program.statusTiers}
                            sellPrices={program.sellPrices ?? []}
                            buyPrices={program.buyPrices ?? []}
                            minimumValueForBuy={program.minimumValueForBuy}
                            maximumValueForBuy={program.maximumValueForBuy}
                            minimumValueForSell={program.minimumValueForSell}
                            maximumValueForSell={program.maximumValueForSell}
                            promoCode={program.promoCode}
                          />
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="block xl:hidden px-4 md:px-6">
                    <TrustIndicators isMobile />
                  </div>
                </div>
              </>
            )}
          </>
        )}
        {!showOfferReady && !isOfferLoading && <Endorsements />}
      </div>

      {/* Email Error Modal */}
      <Dialog open={emailError} onOpenChange={setEmailError}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Something Went Wrong</DialogTitle>
            <DialogDescription>
              We encountered an error while processing your email quote request.
              Please try again or contact support if the problem persists.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setEmailError(false)} className="w-full">
              Try Again
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inactivity Dialog; buyers secure their quote on WhatsApp instead */}
      {program && !(showOfferReady && isBuyQuote) && (
        <InactivityDialog
          open={inactivityDialogOpen}
          onOpenChange={(open) => {
            setInactivityDialogOpen(open);
            // Reset the flag when dialog closes so it can show again after inactivity
            if (!open && hasShownInactivityDialog) {
              setHasShownInactivityDialog(false);
            }
          }}
          onSecureQuote={handleInactivitySecureQuote}
          onEmailDetails={handleInactivityEmailQuote}
        />
      )}

      {!showOfferReady && !isOfferLoading && (
        <FloatingCTAButton offersRef={offersRef as RefObject<HTMLDivElement>} />
      )}
    </div>
  );
}
